import {
  AlertTriangle,
  Announcement01,
  ArrowDown,
  ArrowUp,
  BarChart01,
  Bell01,
  BookOpen01,
  Briefcase01,
  Building02,
  Calendar,
  CheckCircle,
  Clipboard,
  Clock,
  CurrencyDollar,
  Download01,
  File02,
  File06,
  Flag01,
  Folder,
  Gift01,
  Globe01,
  Heart,
  Image01,
  Key01,
  Lightbulb01,
  LineChartUp01,
  Link01,
  Lock01,
  Mail01,
  MessageCircle01,
  Paperclip,
  Phone01,
  PieChart01,
  Rocket01,
  Rss01,
  SearchLg,
  Settings01,
  Star01,
  Tag01,
  Target04,
  ThumbsDown,
  ThumbsUp,
  Trophy01,
  Upload01,
  User01,
  Users02,
  XCircle,
  Zap,
} from '@untitledui/icons';
import React from 'react';

type IconComponent = React.ComponentType<{ className?: string }>;

const EMOJI_TO_ICON: Record<string, IconComponent> = {
  // Charts & Data
  '📊': BarChart01,
  '📈': LineChartUp01,
  '📉': PieChart01,

  // Documents
  '📄': File02,
  '📋': Clipboard,
  '📝': File06,

  // Media & Alerts
  '🖼️': Image01,
  '💡': Lightbulb01,
  '⚠️': AlertTriangle,

  // Status
  '✅': CheckCircle,
  '❌': XCircle,
  '🔔': Bell01,

  // Organization
  '📅': Calendar,
  '🏷️': Tag01,
  '⭐': Star01,
  '🎯': Target04,
  '🚩': Flag01,

  // Communication
  '💬': MessageCircle01,
  '🔗': Link01,
  '📎': Paperclip,
  '📞': Phone01,

  // Business
  '💰': CurrencyDollar,
  '📧': Mail01,
  '👤': User01,
  '🏢': Building02,

  // Tools
  '🔍': SearchLg,
  '⚙️': Settings01,
  '🔒': Lock01,
  '🔑': Key01,

  // Files
  '📁': Folder,
  '📤': Upload01,
  '📥': Download01,

  // Time & Direction
  '🕐': Clock,
  '⬆️': ArrowUp,
  '⬇️': ArrowDown,

  // Web
  '🌐': Globe01,

  // Reactions
  '❤️': Heart,
  '👍': ThumbsUp,
  '👎': ThumbsDown,

  // Energy & Action
  '⚡': Zap,
  '🚀': Rocket01,

  // Research & Business
  '📰': Rss01,
  '🤝': Users02,
  '💼': Briefcase01,
  '🏆': Trophy01,
  '🎉': Gift01,
  '📣': Announcement01,
  '📖': BookOpen01,
};

// Build regex once for performance
const EMOJI_REGEX = new RegExp(
  `(${Object.keys(EMOJI_TO_ICON)
    .map(e => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})`,
  'g'
);

// Common TLDs for URL auto-detection (without requiring protocol)
const COMMON_TLDS = 'com|org|net|io|dev|ai|co|uk|edu|gov';

// URL pattern: matches explicit http(s) URLs OR domain.tld patterns
// Captures URLs like: https://example.com, linkedin.com/in/user, example.io
// Note: The pattern may capture trailing punctuation; cleanUrl() strips it before use
// Negative lookbehind (?<![\\w@.-]) ensures bare domains don't match within email addresses
// by requiring no preceding word char, @, dot, or hyphen (e.g., user@foo-bar.com won't match bar.com)
const URL_PATTERN = `https?:\\/\\/[^\\s]+|(?<![\\w@.-])[\\w-]+(?:\\.[\\w-]+)*\\.(?:${COMMON_TLDS})(?:\\/[^\\s]*)?`;

/**
 * Strips trailing punctuation from a captured URL while preserving balanced delimiters.
 * Handles Wikipedia-style URLs: "https://en.wikipedia.org/wiki/Foo_(bar)" keeps the final ")"
 * But "linkedin.com." → "linkedin.com" and "example.com/path)." → "example.com/path)"
 */
function cleanUrl(url: string): { url: string; suffix: string } {
  let suffix = '';
  let result = url;

  // Process trailing characters one by one from the end
  while (result.length > 0) {
    const lastChar = result[result.length - 1];

    // Simple punctuation: always strip
    if (['.', ',', ';', ':', '!', '?'].includes(lastChar)) {
      suffix = lastChar + suffix;
      result = result.slice(0, -1);
      continue;
    }

    // Closing brackets: only strip if unbalanced (more closers than openers)
    if (lastChar === ')') {
      const opens = (result.match(/\(/g) || []).length;
      const closes = (result.match(/\)/g) || []).length;
      if (closes > opens) {
        suffix = lastChar + suffix;
        result = result.slice(0, -1);
        continue;
      }
    }

    if (lastChar === ']') {
      const opens = (result.match(/\[/g) || []).length;
      const closes = (result.match(/\]/g) || []).length;
      if (closes > opens) {
        suffix = lastChar + suffix;
        result = result.slice(0, -1);
        continue;
      }
    }

    // Not strippable punctuation, stop processing
    break;
  }

  return { url: result, suffix };
}

/**
 * Ensures URL has a protocol prefix for href attribute
 */
function ensureProtocol(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return `https://${url}`;
}

/**
 * Formats URL for display (strips protocol)
 */
function formatDisplayUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '');
}

// Track active highlight timeouts and original background colors to prevent memory leaks
const activeHighlightTimeouts = new Map<string, NodeJS.Timeout>();
const originalBackgrounds = new Map<string, string>();

/**
 * Handles citation click events: scrolls to source, focuses it, and applies temporary highlight.
 */
function handleCitationClick(e: React.MouseEvent, sourceNum: string): void {
  e.preventDefault();

  const manifestEl = (e.currentTarget as HTMLElement).closest(
    '[data-manifest-id]'
  );
  const manifestId = manifestEl?.getAttribute('data-manifest-id');
  const targetId = manifestId
    ? `${manifestId}-source-${sourceNum}`
    : `source-${sourceNum}`;
  const targetEl = document.getElementById(targetId);

  if (targetEl) {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    targetEl.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });

    targetEl.focus();

    // Clear existing timeout for this target to prevent accumulation
    if (activeHighlightTimeouts.has(targetId)) {
      clearTimeout(activeHighlightTimeouts.get(targetId));
    }

    // Capture original background only if not already highlighted
    if (!originalBackgrounds.has(targetId)) {
      originalBackgrounds.set(targetId, targetEl.style.backgroundColor);
    }
    const originalBg = originalBackgrounds.get(targetId) ?? '';

    targetEl.style.backgroundColor = 'rgba(239, 248, 255, 1)';
    const timeoutId = setTimeout(() => {
      if (targetEl.isConnected) {
        targetEl.style.backgroundColor = originalBg;
      }
      activeHighlightTimeouts.delete(targetId);
      originalBackgrounds.delete(targetId);
    }, 2000);
    activeHighlightTimeouts.set(targetId, timeoutId);
  } else {
    console.warn(`Citation target source-${sourceNum} not found in manifest`);
  }
}

/**
 * Renders inline markdown: **bold**, *italic*, `code`, [links](url), and auto-detected URLs
 */
export function renderMarkdown(
  text: string,
  key: string | number,
  manifestId?: string
): React.ReactNode {
  if (typeof text !== 'string') {
    return null;
  }
  if (!text) {
    return null;
  }

  // Combined regex for markdown patterns + bare URLs
  // Order matters: citation \[\d+\](?!\() must come BEFORE link pattern
  // URL pattern comes last to not interfere with markdown link syntax
  const markdownRegex = new RegExp(
    `(\\*\\*[^*]+\\*\\*|\\*[^*]+\\*|\`[^\`]+\`|\\[\\d+\\](?!\\()|\\[[^\\]]+\\]\\([^)]+\\)|${URL_PATTERN})`,
    'g'
  );
  const parts = text.split(markdownRegex);

  if (parts.length === 1) {
    return text;
  }

  // URL detection regex for matching captured parts
  const urlRegex = new RegExp(`^(?:${URL_PATTERN})$`, 'i');

  return parts.map((part, index) => {
    if (!part) {
      return null;
    }

    // Bold: **text**
    if (part.startsWith('**') && part.endsWith('**')) {
      // eslint-disable-next-line react/no-array-index-key -- Text fragments from regex split have no stable identity
      return <strong key={`${key}-${index}`}>{part.slice(2, -2)}</strong>;
    }

    // Italic: *text*
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
      // eslint-disable-next-line react/no-array-index-key -- Text fragments from regex split have no stable identity
      return <em key={`${key}-${index}`}>{part.slice(1, -1)}</em>;
    }

    // Code: `text`
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          // eslint-disable-next-line react/no-array-index-key -- Text fragments from regex split have no stable identity
          key={`${key}-${index}`}
          className='bg-secondary px-1 py-0.5 rounded text-[13px] font-mono'
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    // Citation: [N] (but not [text](url))
    const citationMatch = part.match(/^\[(\d+)\]$/);
    if (citationMatch) {
      const sourceNum = citationMatch[1];
      const sourceId = manifestId
        ? `${manifestId}-source-${sourceNum}`
        : `source-${sourceNum}`;
      return (
        // eslint-disable-next-line react/no-array-index-key -- Text fragments from regex split have no stable identity
        <sup key={`${key}-${index}`} className='inline-flex'>
          <a
            href={`#${sourceId}`}
            onClick={e => handleCitationClick(e, sourceNum)}
            role='doc-noteref'
            aria-label={`Citation ${sourceNum}, jump to source`}
            className='text-utility-brand-600 dark:text-utility-brand-700 hover:text-utility-brand-700 text-[10px] font-medium cursor-pointer no-underline hover:underline ml-[1px] mr-[1px]'
          >
            {sourceNum}
          </a>
        </sup>
      );
    }

    // Link: [text](url)
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [fullMatch, linkText, url] = linkMatch;
      // Only allow safe URLs (http/https)
      if (/^https?:\/\//i.test(url)) {
        return (
          <a
            // eslint-disable-next-line react/no-array-index-key -- Text fragments from regex split have no stable identity
            key={`${key}-${index}`}
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary hover:underline'
          >
            {linkText}
          </a>
        );
      }
      // For unsafe URLs, render the raw markdown syntax as plain text
      return fullMatch;
    }

    // Bare URL: linkedin.com/in/user or https://example.com
    if (urlRegex.test(part)) {
      // Strip trailing punctuation to avoid broken links (e.g., "linkedin.com." → "linkedin.com")
      const { url: cleanedUrl, suffix } = cleanUrl(part);
      const href = ensureProtocol(cleanedUrl);
      const displayText = formatDisplayUrl(cleanedUrl);
      return (
        // eslint-disable-next-line react/no-array-index-key -- Text fragments from regex split have no stable identity
        <React.Fragment key={`${key}-${index}`}>
          <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary hover:underline'
          >
            {displayText}
          </a>
          {suffix}
        </React.Fragment>
      );
    }

    return part;
  });
}

/**
 * Renders text with emojis replaced by Untitled UI icons and markdown formatting.
 * Supports: **bold**, *italic*, `code`, [links](url), and emoji icons.
 */
export function renderTextWithIcons(
  text: string,
  iconClassName = 'w-4 h-4 inline-block relative -top-[2px] mr-1',
  manifestId?: string
): React.ReactNode {
  if (typeof text !== 'string') {
    return null;
  }
  if (!text) {
    return text;
  }

  // First, split by emojis
  const parts = text.split(EMOJI_REGEX);

  // If no emojis found, just render markdown
  if (parts.length === 1) {
    return renderMarkdown(text, 0, manifestId);
  }

  return parts.map((part, index) => {
    const IconComponent = EMOJI_TO_ICON[part];
    if (IconComponent) {
      // eslint-disable-next-line react/no-array-index-key -- Text fragments from emoji regex split have no stable identity
      return <IconComponent key={index} className={iconClassName} />;
    }
    // Render markdown for text parts
    return (
      // eslint-disable-next-line react/no-array-index-key -- Text fragments from emoji regex split have no stable identity
      <React.Fragment key={index}>
        {renderMarkdown(part, index, manifestId)}
      </React.Fragment>
    );
  });
}

export default renderTextWithIcons;
